/**
 * Renderer générique de sélection avec recherche serveur.
 *
 * Ce composant ne déclenche aucun appel réseau lui-même. Il affiche les
 * éléments sélectionnés, un champ de recherche contrôlé et les résultats
 * fournis par le parent, puis délègue les actions via callbacks.
 *
 * Le parent reste responsable de l'ordre des requêtes, de l'ignorance des
 * réponses obsolètes et des champs cachés propres au domaine via `hiddenHtml`.
 */
import { renderLucideIcon } from '../utils/lucide-icons.js'

/**
 * Rend le composant complet : en-tête, éléments sélectionnés, champ de
 * recherche et résultats fournis par le parent.
 */
function renderServerSearchSelect(options) {
  options = normalizeOptions(options)
  var searchLabelClassAttr = options.searchLabelClassName
    ? ' class="' + escapeAttributeValue(options.searchLabelClassName) + '"'
    : ''
  var rootAttrs = [
    'class="wc-server-search-select"',
    'data-server-search-select',
    'data-server-search-name="' + escapeAttributeValue(options.name) + '"'
  ].join(' ')
  return (
    '<div ' + rootAttrs + '>' +
      '<div class="wc-server-search-select-head">' +
        '<div class="' + escapeAttributeValue(options.labelClassName) + '">' + options.icon + '<span>' + escapeHtml(options.label) + '</span></div>' +
        (options.help ? '<div class="' + escapeAttributeValue(options.helpClassName) + '">' + escapeHtml(options.help) + '</div>' : '') +
        (options.noticeHtml ? '<div class="wc-server-search-select-notice">' + options.noticeHtml + '</div>' : '') +
      '</div>' +
      '<div class="wc-server-search-select-selected" data-server-search-selected-list>' +
        (options.selectedItems.length
          ? options.selectedItems.map(function (item) { return renderServerSearchSelectedItem(options, item) }).join('')
          : '<div class="wc-server-search-select-empty" data-server-search-empty-selected>' + escapeHtml(options.emptySelectedLabel) + '</div>') +
      '</div>' +
      (!options.disabled && !options.hideSearch
        ? '<div class="wc-server-search-select-search">' +
            '<label class="wc-server-search-select-field">' +
              '<span' + searchLabelClassAttr + '>' + escapeHtml(options.searchLabel) + '</span>' +
              '<input type="search" data-server-search-input value="' + escapeAttributeValue(options.query) + '" placeholder="' + escapeAttributeValue(options.searchPlaceholder) + '" autocomplete="off">' +
            '</label>' +
            '<div class="wc-server-search-select-results" data-server-search-results>' +
              renderServerSearchResults(options, options.resultItems) +
            '</div>' +
          '</div>'
        : '') +
    '</div>'
  )
}

/**
 * Rend un élément déjà sélectionné, avec ses métadonnées, alertes éventuelles
 * et le bouton de retrait lorsque le composant est éditable.
 */
function renderServerSearchSelectedItem(options, item) {
  item = normalizeItem(item)
  var meta = item.meta ? '<div class="wc-server-search-select-item-meta">' + escapeHtml(item.meta) + '</div>' : ''
  var warning = item.warning
    ? '<div class="wc-server-search-select-item-warning">' + renderLucideIcon('triangleAlert') + '<span>' + escapeHtml(item.warning) + '</span></div>'
    : ''
  var blocked = item.blockedReason
    ? '<div class="wc-server-search-select-item-blocked">' + renderLucideIcon('circleAlert') + '<span>' + escapeHtml(item.blockedReason) + '</span></div>'
    : ''

  return (
    '<div class="wc-server-search-select-item selected" data-server-search-selected-item data-item-id="' + escapeAttributeValue(item.id) + '">' +
      (item.hiddenHtml || '') +
      '<span class="wc-server-search-select-item-body">' +
        '<span class="wc-server-search-select-item-name">' + escapeHtml(item.label || item.id) + '</span>' +
        meta +
        warning +
        blocked +
      '</span>' +
      (options.disabled || item.locked ? '' : '<button type="button" class="wc-server-search-select-remove" data-server-search-remove aria-label="Retirer ' + escapeAttributeValue(item.label || item.id) + '">' + renderLucideIcon('x') + '</button>') +
    '</div>'
  )
}

/**
 * Rend la zone de résultats de recherche : état d'attente, état vide,
 * chargement ou boutons de sélection.
 */
function renderServerSearchResults(options, items) {
  items = Array.isArray(items) ? items.map(normalizeItem).filter(function (item) { return item.id }) : []
  if (options.loading) {
    return '<div class="wc-server-search-select-results-state">' + escapeHtml(options.loadingLabel) + '</div>'
  }
  if (!options.query) {
    return '<div class="wc-server-search-select-results-state">' + escapeHtml(options.idleLabel) + '</div>'
  }
  if (!items.length) {
    return '<div class="wc-server-search-select-results-state">' + escapeHtml(options.emptyResultsLabel) + '</div>'
  }
  return items.map(function (item) {
    var meta = item.meta ? '<span class="wc-server-search-select-result-meta">' + escapeHtml(item.meta) + '</span>' : ''
    return (
      '<button type="button" class="wc-server-search-select-result" data-server-search-result data-item-id="' + escapeAttributeValue(item.id) + '"' + (item.disabled ? ' disabled' : '') + '>' +
        '<span class="wc-server-search-select-result-main">' +
          '<span class="wc-server-search-select-result-label">' + escapeHtml(item.label || item.id) + '</span>' +
          meta +
        '</span>' +
        renderLucideIcon('plus') +
      '</button>'
    )
  }).join('')
}

/**
 * Branche les événements DOM du composant rendu vers les callbacks fournis par
 * le parent.
 */
function bindServerSearchSelect(el, options, handlers) {
  var root = el && el.querySelector ? el.querySelector('[data-server-search-select][data-server-search-name="' + escapeSelectorValue(options && options.name) + '"]') : null
  if (!root) return
  options = normalizeOptions(options)
  bindServerSearchRemoveButtons(root, handlers)
  bindServerSearchResultButtons(root, handlers)
  var input = root.querySelector('[data-server-search-input]')
  if (!input) return
  var timer = null
  // Le debounce ne temporise que le signal UI : le parent conserve la requête
  // courante de référence et décide quelles réponses serveur appliquer.
  input.addEventListener('input', function () {
    if (!handlers || typeof handlers.onSearch !== 'function') return
    if (timer) clearTimeout(timer)
    timer = setTimeout(function () {
      handlers.onSearch({ query: String(input.value || '').trim() })
    }, options.debounceMs)
  })
}

/**
 * Branche les boutons de retrait des éléments sélectionnés.
 */
function bindServerSearchRemoveButtons(root, handlers) {
  Array.prototype.slice.call(root.querySelectorAll('[data-server-search-remove]')).forEach(function (button) {
    if (button.getAttribute('data-bound') === 'true') return
    button.setAttribute('data-bound', 'true')
    button.addEventListener('click', function () {
      var item = findClosest(button, '[data-server-search-selected-item]')
      var id = item ? String(item.getAttribute('data-item-id') || '').trim() : ''
      if (handlers && typeof handlers.onRemove === 'function') handlers.onRemove({ id: id, itemEl: item })
    })
  })
}

/**
 * Branche les boutons de résultats afin de signaler la sélection d'un item au
 * parent.
 */
function bindServerSearchResultButtons(root, handlers) {
  Array.prototype.slice.call(root.querySelectorAll('[data-server-search-result]')).forEach(function (button) {
    if (button.getAttribute('data-bound') === 'true') return
    button.setAttribute('data-bound', 'true')
    button.addEventListener('click', function () {
      var id = String(button.getAttribute('data-item-id') || '').trim()
      if (handlers && typeof handlers.onSelect === 'function') handlers.onSelect({ id: id, resultEl: button })
    })
  })
}

/**
 * Applique les valeurs par défaut du composant et normalise les collections
 * d'items.
 */
function normalizeOptions(options) {
  options = options || {}
  return {
    name: String(options.name || 'server-search-select'),
    label: String(options.label || ''),
    help: String(options.help || ''),
    noticeHtml: String(options.noticeHtml || ''),
    labelClassName: String(options.labelClassName || 'wc-server-search-select-label'),
    helpClassName: String(options.helpClassName || 'wc-server-search-select-help'),
    searchLabelClassName: String(options.searchLabelClassName || ''),
    icon: String(options.icon || ''),
    query: String(options.query || ''),
    selectedItems: Array.isArray(options.selectedItems) ? options.selectedItems.map(normalizeItem).filter(function (item) { return item.id }) : [],
    resultItems: Array.isArray(options.resultItems) ? options.resultItems.map(normalizeItem).filter(function (item) { return item.id }) : [],
    disabled: options.disabled === true,
    hideSearch: options.hideSearch === true,
    loading: options.loading === true,
    searchLabel: String(options.searchLabel || 'Rechercher'),
    searchPlaceholder: String(options.searchPlaceholder || 'Rechercher'),
    emptySelectedLabel: String(options.emptySelectedLabel || 'Aucun élément sélectionné.'),
    emptyResultsLabel: String(options.emptyResultsLabel || 'Aucun résultat.'),
    idleLabel: String(options.idleLabel || 'Saisissez une recherche.'),
    loadingLabel: String(options.loadingLabel || 'Recherche...'),
    debounceMs: Number.isFinite(options.debounceMs) ? Math.max(0, options.debounceMs) : 260
  }
}

/**
 * Normalise un item pour garantir un contrat stable au rendu.
 */
function normalizeItem(item) {
  item = item || {}
  return {
    id: String(item.id || '').trim(),
    label: String(item.label || '').trim(),
    meta: String(item.meta || '').trim(),
    warning: String(item.warning || '').trim(),
    blockedReason: String(item.blockedReason || '').trim(),
    // Permet aux adaptateurs de conserver la sémantique de submit du formulaire
    // sans coupler ce composant à un modèle métier précis.
    hiddenHtml: String(item.hiddenHtml || ''),
    disabled: item.disabled === true,
    locked: item.locked === true
  }
}

/**
 * Remonte l'arbre DOM jusqu'au premier parent correspondant au sélecteur.
 */
function findClosest(el, selector) {
  var current = el
  while (current) {
    if (current.matches && current.matches(selector)) return current
    current = current.parentNode
  }
  return null
}

/**
 * Échappe une valeur destinée à du contenu HTML texte.
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Échappe une valeur destinée à un attribut HTML.
 */
function escapeAttributeValue(value) {
  return escapeHtml(value).replace(/`/g, '&#96;')
}

/**
 * Échappe une valeur utilisée dans un sélecteur CSS attributaire.
 */
function escapeSelectorValue(value) {
  return String(value || '').replace(/["\\]/g, '\\$&')
}

export {
  bindServerSearchSelect,
  renderServerSearchSelect,
  renderServerSearchSelectedItem
}
